import { NumberValue } from 'd3' // Import the NumberValue type
import {
  dayFormatter,
  dayHourFormatter,
  monthYearDayFormatter,
  monthYearDayShortFormatter,
} from 'utils/formatChartTimes'

export type SnapshotType = {
  id: string
  timestamp: string | number
  totalValueLockedUSD: string
  tvlToken0: string
  tvlToken1: string
  _token0Amount: string
  _token1Amount: string
  _token0AmountUSD: string
  _token1AmountUSD: string
  upperTick: string
  lowerTick: string
  volumeUSD: string
  // volumeUSD # 24 hour possible?
  apr: string
  outputTokenPriceUSD: string
  vault: {
    managerFee: string
    upperTick: string
    lowerTick: string
    apr: {
      averageApr: string
    }
    pool: {
      volumeUSD: string
      volumeToken0: string
      volumeToken1: string
    }
    _token0AmountUSD: string
    _token1AmountUSD: string
    _token0Amount: string
    _token1Amount: string
    _token0: {
      id: string
      symbol: string
      volume: string
      volumeUSD: string
      totalValueLocked: string
      totalValueLockedUSD: string
    }
    _token1: {
      id: string
      symbol: string
      volume: string
      volumeUSD: string
      totalValueLocked: string
      totalValueLockedUSD: string
    }
  }
}

export function selectDataKey(snapshot: SnapshotType, unit: 'token0' | 'token1' | 'both'): number {
  switch (unit) {
    case 'token0':
      return parseFloat(snapshot.tvlToken0)
    case 'token1':
      return parseFloat(snapshot.tvlToken1)
    default:
      return parseFloat(snapshot.totalValueLockedUSD)
  }
}

export function selectXAxisTimeFormat(timeframe: '7' | '30' | '90' | 'max'): (timestamp: NumberValue) => string {
  let timestampFormatter
  switch (timeframe) {
    case 'max':
      timestampFormatter = monthYearDayFormatter('en-US') // for longer timeframes, show month and day
      break
    default:
      timestampFormatter = dayFormatter('en-US')
      break
  }
  return timestampFormatter
}

// used for island analytics tooltips
export function selectTimeFormat(timeframe: '7' | '30' | '90' | 'max'): (timestamp: NumberValue) => string {
  let timestampFormatter
  switch (timeframe) {
    case 'max':
      timestampFormatter = monthYearDayShortFormatter('en-US') // for longer timeframes, show month and day
      break
    default:
      timestampFormatter = dayHourFormatter('en-US') // for a short timeframe, show hours
      break
  }
  return timestampFormatter
}

export function filterDataByTimeframe<T extends { timestamp: string | number }>(
  data: T[],
  timeframe: '7' | '30' | '90' | 'max'
): T[] {
  if (!data || data.length === 0) {
    return []
  }

  const timestamps = data.map((snapshot) => parseFloat(snapshot.timestamp.toString()))
  const mostRecentTimestamp = Math.max(...timestamps)
  const oldestTimestamp = Math.min(...timestamps)
  let startTime: number

  switch (timeframe) {
    case '7':
      startTime = mostRecentTimestamp - 7 * 86400
      break
    case '30':
      startTime = mostRecentTimestamp - 30 * 86400
      break
    case '90':
      startTime = mostRecentTimestamp - 90 * 86400
      break
    default:
      startTime = oldestTimestamp
  }

  return data.filter((snapshot) => parseFloat(snapshot.timestamp.toString()) >= startTime)
}
